import { addEditableNote, DEFAULT_DRAW_VELOCITY, snapBeat } from "./midi-editor.ts";
import { resolveEditableNoteWithVoice } from "./rhythm-slots.ts";
import { isUnresolvedMidiNote, type MidiNote, type PitchFrame } from "./music.ts";
import {
  advanceVoicePitchFrame,
  EMPTY_VOICE_DISPLAY_STATE,
  type VoiceDisplayState,
} from "./note-events.ts";

export type VoiceDrawPhase =
  | "idle"
  | "ready"
  | "gesture-active"
  | "pitch-searching"
  | "pitch-locked"
  | "commit"
  | "cancel";

export type VoiceDrawRange = {
  startBeat: number;
  endBeat: number;
  durationBeats: number;
  gridCount: number;
};

export type VoiceDrawState = {
  phase: VoiceDrawPhase;
  pointerId: number | null;
  targetNoteIndex: number | null;
  anchorBeat: number;
  currentBeat: number;
  range: VoiceDrawRange | null;
  frames: PitchFrame[];
  stabilizer: VoiceDisplayState;
  candidateMidi: number | null;
  lockedMidi: number | null;
  cents: number | null;
  validFrameCount: number;
  stableDurationMs: number;
  message: string;
};

export type VoiceDrawPitchOptions = {
  confidenceThreshold: number;
  minimumLevel: number;
  minimumFrames?: number;
  minimumStableMs?: number;
  minimumWinnerShare?: number;
};

export type VoiceDrawCommitResult = {
  committed: boolean;
  action: "add" | "repitch" | "cancel";
  before: MidiNote[];
  notes: MidiNote[];
  selectedNoteIndex: number | null;
  midi: number | null;
  message: string;
  state: VoiceDrawState;
};

const cloneNotes = (notes: MidiNote[]) => notes.map((note) => ({ ...note }));
const clamp = (value: number, minimum: number, maximum: number) => Math.max(minimum, Math.min(maximum, value));
const gesturePhases = new Set<VoiceDrawPhase>(["gesture-active", "pitch-searching", "pitch-locked"]);

export function emptyVoiceDrawState(phase: VoiceDrawPhase = "idle", message = "VOICE OFF"): VoiceDrawState {
  return {
    phase,
    pointerId: null,
    targetNoteIndex: null,
    anchorBeat: 0,
    currentBeat: 0,
    range: null,
    frames: [],
    stabilizer: { ...EMPTY_VOICE_DISPLAY_STATE },
    candidateMidi: null,
    lockedMidi: null,
    cents: null,
    validFrameCount: 0,
    stableDurationMs: 0,
    message,
  };
}

export function readyVoiceDrawState(message = "VOICE READY · 指で押している間だけ声を読み取ります"): VoiceDrawState {
  return emptyVoiceDrawState("ready", message);
}

export function voiceDrawBeatFromX(x: number, width: number, totalBeats: number): number {
  return clamp(x / Math.max(1, width), 0, 1) * Math.max(0, totalBeats);
}

export function voiceDrawRange(anchorBeat: number, currentBeat: number, gridStep: number, totalBeats: number): VoiceDrawRange {
  const safeGrid = Math.max(0.0001, gridStep);
  const maximum = Math.max(safeGrid, totalBeats);
  const anchor = clamp(snapBeat(anchorBeat, safeGrid), 0, Math.max(0, maximum - safeGrid));
  const current = clamp(snapBeat(currentBeat, safeGrid), 0, maximum);
  let startBeat: number;
  let endBeat: number;
  if (current < anchor) {
    startBeat = current;
    endBeat = anchor;
  } else {
    startBeat = anchor;
    endBeat = Math.max(anchor + safeGrid, current);
  }
  endBeat = clamp(endBeat, safeGrid, maximum);
  startBeat = clamp(Math.min(startBeat, endBeat - safeGrid), 0, maximum - safeGrid);
  const durationBeats = Math.max(safeGrid, endBeat - startBeat);
  return {
    startBeat,
    endBeat: startBeat + durationBeats,
    durationBeats,
    gridCount: Math.max(1, Math.round(durationBeats / safeGrid)),
  };
}

export function beginVoiceDrawGesture(
  state: VoiceDrawState,
  pointerId: number,
  anchorBeat: number,
  targetNoteIndex: number | null,
  gridStep: number,
  totalBeats: number,
): VoiceDrawState {
  if (state.phase !== "ready") return state;
  return {
    ...emptyVoiceDrawState("gesture-active", targetNoteIndex === null ? "PITCH SEARCHING" : "REPITCH SEARCHING"),
    pointerId,
    targetNoteIndex,
    anchorBeat,
    currentBeat: anchorBeat,
    range: targetNoteIndex === null ? voiceDrawRange(anchorBeat, anchorBeat, gridStep, totalBeats) : null,
  };
}

export function updateVoiceDrawGesture(
  state: VoiceDrawState,
  pointerId: number,
  currentBeat: number,
  gridStep: number,
  totalBeats: number,
): VoiceDrawState {
  if (!gesturePhases.has(state.phase) || state.pointerId !== pointerId) return state;
  return {
    ...state,
    currentBeat,
    range: state.targetNoteIndex === null
      ? voiceDrawRange(state.anchorBeat, currentBeat, gridStep, totalBeats)
      : state.range,
  };
}

function acceptedFrame(frame: PitchFrame, options: VoiceDrawPitchOptions): boolean {
  const confidence = Math.max(options.confidenceThreshold, frame.acceptanceConfidence ?? 0);
  return frame.midiFloat !== null
    && Number.isFinite(frame.midiFloat)
    && frame.confidence >= confidence
    && (frame.level ?? 0) >= options.minimumLevel;
}

export function summarizeVoiceDrawPitch(frames: PitchFrame[], options: VoiceDrawPitchOptions) {
  const valid = frames.filter((frame) => acceptedFrame(frame, options));
  if (!valid.length) {
    return { candidateMidi: null, lockedMidi: null, cents: null, validFrameCount: 0, stableDurationMs: 0 };
  }
  const scores = new Map<number, number>();
  valid.forEach((frame) => {
    const midi = Math.round(frame.midiFloat as number);
    scores.set(midi, (scores.get(midi) ?? 0) + Math.max(0.01, frame.confidence));
  });
  const candidateMidi = [...scores.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0][0];
  const winningFrames = valid.filter((frame) => Math.round(frame.midiFloat as number) === candidateMidi);
  const totalWeight = [...scores.values()].reduce((sum, value) => sum + value, 0);
  const winningWeight = scores.get(candidateMidi) ?? 0;
  const sortedCents = winningFrames
    .map((frame) => ((frame.midiFloat as number) - candidateMidi) * 100)
    .sort((a, b) => a - b);
  const cents = sortedCents[Math.floor(sortedCents.length / 2)] ?? 0;
  const stableDurationMs = winningFrames.length > 1
    ? Math.max(0, (winningFrames.at(-1)!.time - winningFrames[0].time) * 1000)
    : 0;
  const spread = sortedCents.length ? sortedCents.at(-1)! - sortedCents[0] : Number.POSITIVE_INFINITY;
  const locked = winningFrames.length >= (options.minimumFrames ?? 3)
    && stableDurationMs >= (options.minimumStableMs ?? 90)
    && winningWeight / Math.max(0.0001, totalWeight) >= (options.minimumWinnerShare ?? 0.62)
    && spread <= 70;
  return {
    candidateMidi,
    lockedMidi: locked ? candidateMidi : null,
    cents: Math.round(cents),
    validFrameCount: winningFrames.length,
    stableDurationMs: Math.round(stableDurationMs),
  };
}

export function addVoiceDrawFrame(
  state: VoiceDrawState,
  frame: PitchFrame,
  options: VoiceDrawPitchOptions,
): VoiceDrawState {
  if (!gesturePhases.has(state.phase) || state.pointerId === null) return state;
  const hasLevel = (frame.level ?? 0) >= options.minimumLevel;
  const stabilized = advanceVoicePitchFrame(
    state.stabilizer,
    hasLevel ? frame : null,
    options.confidenceThreshold,
    2,
    3,
  );
  const stableFrame = stabilized.stableNote !== null
    && acceptedFrame(frame, options)
    && Math.abs((frame.midiFloat as number) - stabilized.stableNote) < 0.66;
  const frames = stableFrame ? [...state.frames.slice(-95), frame] : state.frames;
  const summary = summarizeVoiceDrawPitch(frames, options);
  const rawCandidate = acceptedFrame(frame, options) ? Math.round(frame.midiFloat as number) : null;
  const candidateMidi = summary.candidateMidi ?? rawCandidate;
  return {
    ...state,
    phase: summary.lockedMidi === null ? "pitch-searching" : "pitch-locked",
    frames,
    stabilizer: stabilized,
    candidateMidi,
    lockedMidi: summary.lockedMidi,
    cents: summary.cents,
    validFrameCount: summary.validFrameCount,
    stableDurationMs: summary.stableDurationMs,
    message: summary.lockedMidi === null ? (candidateMidi === null ? "PITCH SEARCHING" : "PITCH DETECTING") : "PITCH LOCK",
  };
}

export function cancelVoiceDrawGesture(state: VoiceDrawState, message = "VOICE DRAWをキャンセルしました"): VoiceDrawState {
  return { ...emptyVoiceDrawState("cancel", message), anchorBeat: state.anchorBeat, currentBeat: state.currentBeat };
}

export function voiceDrawMicFailure(message = "マイクを開始できませんでした"): VoiceDrawState {
  return emptyVoiceDrawState("cancel", message);
}

export function isVoiceDrawFingerArmed(state: VoiceDrawState): boolean {
  return state.pointerId !== null && gesturePhases.has(state.phase);
}

export function voiceDrawShouldPausePlayback(isPlaying: boolean, state: VoiceDrawState): boolean {
  return isPlaying && state.phase === "ready";
}

export function commitVoiceDrawGesture(
  state: VoiceDrawState,
  notes: MidiNote[],
  options: VoiceDrawPitchOptions,
): VoiceDrawCommitResult {
  const before = cloneNotes(notes);
  if (!isVoiceDrawFingerArmed(state)) {
    return {
      committed: false,
      action: "cancel",
      before,
      notes: before,
      selectedNoteIndex: null,
      midi: null,
      message: "指で操作していないため変更しませんでした",
      state: cancelVoiceDrawGesture(state, "Finger Gateなしでは変更しません"),
    };
  }
  const summary = summarizeVoiceDrawPitch(state.frames, options);
  if (summary.lockedMidi === null) {
    return {
      committed: false,
      action: "cancel",
      before,
      notes: before,
      selectedNoteIndex: state.targetNoteIndex,
      midi: null,
      message: "Pitchを安定して取得できませんでした",
      state: cancelVoiceDrawGesture(state, "Pitchを安定して取得できませんでした"),
    };
  }
  if (state.targetNoteIndex !== null) {
    const original = notes[state.targetNoteIndex];
    if (!original) {
      return {
        committed: false,
        action: "cancel",
        before,
        notes: before,
        selectedNoteIndex: null,
        midi: null,
        message: "対象ノートを確認できませんでした",
        state: cancelVoiceDrawGesture(state, "対象ノートを確認できませんでした"),
      };
    }
    const after = cloneNotes(notes);
    after[state.targetNoteIndex] = resolveEditableNoteWithVoice(original, summary.lockedMidi);
    // An unresolved slot carries a fallback MIDI value only so it can be drawn.
    // Resolving it to that same pitch is still a real state change.
    const changed = isUnresolvedMidiNote(original) || original.midi !== summary.lockedMidi;
    return {
      committed: changed,
      action: changed ? "repitch" : "cancel",
      before,
      notes: after,
      selectedNoteIndex: state.targetNoteIndex,
      midi: summary.lockedMidi,
      message: changed ? "VOICE REPITCHを反映しました" : "同じPitchのため変更しませんでした",
      state: emptyVoiceDrawState(changed ? "commit" : "cancel", changed ? "REPITCH COMMIT" : "Pitchは変更されていません"),
    };
  }
  if (!state.range) {
    return {
      committed: false,
      action: "cancel",
      before,
      notes: before,
      selectedNoteIndex: null,
      midi: null,
      message: "時間範囲を確認できませんでした",
      state: cancelVoiceDrawGesture(state, "時間範囲を確認できませんでした"),
    };
  }
  const added: MidiNote = {
    midi: summary.lockedMidi,
    startBeat: state.range.startBeat,
    durationBeats: state.range.durationBeats,
    velocity: DEFAULT_DRAW_VELOCITY,
  };
  const after = addEditableNote(notes, [added]);
  const selectedNoteIndex = after.findIndex((note) =>
    note.midi === added.midi
    && note.startBeat === added.startBeat
    && note.durationBeats === added.durationBeats
    && note.velocity === added.velocity);
  return {
    committed: true,
    action: "add",
    before,
    notes: after,
    selectedNoteIndex,
    midi: summary.lockedMidi,
    message: "VOICE DRAWノートを追加しました",
    state: emptyVoiceDrawState("commit", "VOICE DRAW COMMIT"),
  };
}
